import {
  React,
  useCallback,
  NavLink,
  useLocation,
  useNavigate,
  House,
  MessagesSquare,
  UserRound,
  Settings,
  FileUp,
  startTransition,
  useState,
  CircleUserRound,
  useRef,
  useEffect,
  CustomConfirmDialog,
  miniLogo,
  Tooltip,
  HardDrive,
  VaultilakeScopeSelectorPopup,
  Typography,
  CirclePlus,
} from '../../utils/globalImports';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from '../../store/slices/auth';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { ExtendedAccountResponse } from '../../store/types';
import { useLogoutMutation } from '../../api/loginApiSlice';
import { setLakehouseSelection } from '../../store/slices/vaultilake';
import { BookText, Bell, LogOut } from 'lucide-react';

const CustomSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showVaultilakeScopePopup, setShowVaultilakeScopePopup] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth);
  const [logoutApi] = useLogoutMutation();
  const account = auth.account as ExtendedAccountResponse | null;
  const rawRole = account?.user?.role || account?.role;
  const userRole = typeof rawRole === 'string' ? rawRole.trim().toLowerCase() : undefined;
  const username = account?.user?.userName || account?.username || '';
  const email = account?.user?.email || account?.email || '';

  const { selectedCustomerId, selectedClientId } = useSelector((state: RootState) => state.vaultilake);

  useEffect(() => {
    if (userRole === 'clientadmin' && !selectedCustomerId && account) {
      const customerId = account?.user?.customer_id || account?.customer_id || null;
      const clientId = account?.user?.client_id || account?.client_id || null;
      if (customerId && clientId) {
        dispatch(setLakehouseSelection({ customerId, clientId }));
      }
    }
  }, [userRole, account, selectedCustomerId, selectedClientId, dispatch]);

  // Function to format role for display
  const formatRole = (role: string) => {
    if (!role) return '';
    switch (role.toLowerCase()) {
      case 'superadmin':
        return 'Super Admin';
      case 'customeradmin':
        return 'Customer Admin';
      case 'clientadmin':
        return 'Client Admin';
      case 'user':
        return 'User';
      default:
        return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  // Function to get user initials
  const getUserInitials = (name: string) => {
    if (!name) return '';
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  let path = '';
  switch (userRole) {
    case 'superadmin':
      path = '/customer-management';
      break;
    case 'customeradmin':
      path = '/customer-management';
      break;
    case 'clientadmin':
      path = '/client-management';
      break;
    case 'user':
      path = '/user-management';
      break;
  }

  const handleNavigation = useCallback(
    (path: string) => {
      startTransition(() => {
        navigate(path);
      });
    },
    [navigate],
  );

  const menuItems = [
    { name: t('menuItems.dashboard'), path: '/dashboard', icon: House },
    {
      name: 'VaultiScan',
      path: '/chat',
      icon: MessagesSquare,
      renderIcon: () => <img src={miniLogo} alt="VaultiScan" className="sidebar-icon chat-logo-icon" />,
    },
    {
      name: t('menuItems.vaultilake'),
      path: '/data-platform',
      icon: HardDrive,
      renderIcon: () => <img src={miniLogo} alt="VaultiScan" className="sidebar-icon chat-logo-icon" />,
      allowedRoles: ['superadmin', 'customeradmin', 'clientadmin'],
      onClick: () => {
        if (userRole === 'clientadmin') {
          const customerId = account?.user?.customer_id || account?.customer_id || null;
          const clientId = account?.user?.client_id || account?.client_id || null;
          dispatch(setLakehouseSelection({ customerId, clientId }));
          handleNavigation('/data-platform');
        } else {
          setShowVaultilakeScopePopup(true);
        }
      },
    },
    {
      name: t('menuItems.setup'),
      path: path,
      icon: CirclePlus,
      allowedRoles: ['superadmin', 'customeradmin', 'clientadmin', 'user'],
    },
  ];

  const bottomItems = [
    {
      name: t('menuItems.settings'),
      path: '/token-management',
      icon: Settings,
      allowedRoles: ['superadmin', 'customeradmin'],
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const confirmLogout = () => {
    setShowLogoutConfirm(true);
  };

  const logout = async () => {
    try {
      await logoutApi().unwrap();
      startTransition(() => {
        dispatch(authActions.logout());
        navigate('/login');
      });
    } catch (err) {
      console.error('Logout failed:', err);
      dispatch(authActions.logout());
      navigate('/login');
    }
  };

  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev);
  };

  const isCustomerActive = [
    '/customer-management',
    '/client-management',
    '/group-management',
    '/category-management',
    '/user-management',
    '/sdk',
  ].some((path) => location.pathname.startsWith(path));

  const isVaultilakeActive = ['/data-platform', '/lakehouses', '/data-agents', '/vaultilake-chat', '/ontology'].some((path) =>
    location.pathname.startsWith(path),
  );

  const isVaultiscanActive = [
    '/chat',
    '/document-dashboard',
    '/document-upload',
    '/agents',
    '/connectors',
    '/vaulticrawl',
  ].some((path) => location.pathname.startsWith(path));

  const isVaultiCrawlActive = location.pathname.startsWith('/vaulticrawl');

  return (
    <>
      <div className="custom-sidebar">
        {/* Top Menu Icons (logo removed, content moved up) */}
        <div className="custom-sidebar-links">
          {menuItems
            .filter((item) => {
              // Role check
              if (item.allowedRoles && !item.allowedRoles.includes(userRole || '')) {
                return false;
              }

              return true;
            })
            .map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) =>
                  (item.name === t('menuItems.setup') && isCustomerActive) ||
                  (item.name === 'VaultiScan' && isVaultiscanActive) ||
                  (item.name === t('menuItems.vaultilake') && isVaultilakeActive) ||
                  (item.name === 'VaultiCrawl' && isVaultiCrawlActive) ||
                  (item.name === t('menuItems.dashboard') && isActive)
                    ? 'active-link'
                    : ''
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (item.onClick) {
                    item.onClick();
                  } else {
                    handleNavigation(item.path);
                  }
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textDecoration: 'none',
                  gap: 4,
                }}
              >
                {/* Icon */}
                {item.renderIcon ? item.renderIcon() : <item.icon className="sidebar-icon" />}

                {/* Label under icon */}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '10px',
                    lineHeight: 1.2,
                    color: '#475569',
                    textAlign: 'center',
                  }}
                >
                  {item.name}
                </Typography>
              </NavLink>
            ))}
        </div>

        {/* Bottom: Settings + User + Logout */}
        <div className="custom-sidebar-bottom">
          {bottomItems
            .filter((item) => {
              if (!item.allowedRoles) return true;
              return item.allowedRoles.includes(userRole || '');
            })
            .map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                className={({ isActive }) => (isActive ? 'active-link' : '')}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation(item.path);
                }}
              >
                <Tooltip title={item.name} placement="bottom">
                  <item.icon className="sidebar-icon" />
                </Tooltip>
              </NavLink>
            ))}

          {/* User Icon with Dropdown */}
          <div className="sidebar-dropdown-wrapper" ref={dropdownRef}>
            {dropdownVisible && (
              <div
                className="sidebar-dropdown-menu"
                style={{
                  position: 'absolute',
                  top: '-160px',
                  right: '-55px',
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  minWidth: '200px',
                  maxWidth: '320px',
                  width: 'auto',
                  maxHeight: '260px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  padding: '0',
                }}
              >
                {/* Email and Role Section */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                  {email && (
                    <div
                      className="user-email"
                      style={{
                        fontSize: '14px',
                        color: '#666',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: userRole ? '8px' : '0',
                      }}
                      title={email}
                    >
                      {email}
                    </div>
                  )}

                  {/* Role Display */}
                  {userRole && (
                    <div>
                      <span
                        style={{
                          backgroundColor: 'rgba(105, 94, 168, 0.11)',
                          color: '#695ea8',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          display: 'inline-block',
                        }}
                      >
                        {formatRole(userRole)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Dropdown Menu Items */}
                <div>
                  {/* User Guide */}
                  <button
                    onClick={() => {
                      setDropdownVisible(false);
                      navigate('/docs/intro');
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <BookText size={16} />
                    User Guide
                  </button>

                  {/* Notification Settings */}
                  <button
                    onClick={() => {
                      setDropdownVisible(false);
                      navigate('/notifications/preferences');
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Bell size={16} />
                    Notification Settings
                  </button>

                  {/* Logout */}
                  <button
                    onClick={confirmLogout}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <LogOut size={16} />
                    {t('menuItems.logout')}
                  </button>
                </div>
              </div>
            )}
            <div
              className="user-initials"
              onClick={toggleDropdown}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#695ea8',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              {getUserInitials(username) || <CircleUserRound className="sidebar-icon" />}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <CustomConfirmDialog
        open={showLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        onConfirm={() => {
          logout();
          setShowLogoutConfirm(false);
        }}
        onCancel={() => setShowLogoutConfirm(false)}
        onClose={() => setShowLogoutConfirm(false)}
      />

      <VaultilakeScopeSelectorPopup
        open={showVaultilakeScopePopup}
        onClose={() => setShowVaultilakeScopePopup(false)}
      />
    </>
  );
};

export default CustomSidebar;
